import { useState, useRef } from 'react';
import { 
  Camera, 
  X, 
  Star, 
  Plus,
  GripVertical,
  Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '../lib/toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function MultiPhotoUploader({ 
  images = [], 
  primaryIndex = 0, 
  onImagesChange, 
  onPrimaryChange,
  maxImages = 6,
  api,
  compact = false // When true, only shows the big upload dialog (no small "Add Photo" grid button)
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  
  // Get the display URL for an image (handles both old string format and new object format)
  const getDisplayUrl = (image) => {
    // New object format: {ref, url, expires_at}
    if (typeof image === 'object' && image !== null) {
      return image.url || image.ref;
    }
    // Old string format
    if (typeof image === 'string') {
      if (image.startsWith('http')) {
        return image;
      }
      if (image.startsWith('/api/')) {
        return `${API_URL}${image}`;
      }
      // gcs:// refs shouldn't reach here, but handle gracefully
      if (image.startsWith('gcs://')) {
        return '/placeholder-image.png';
      }
      return image;
    }
    return '/placeholder-image.png';
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const remainingSlots = maxImages - images.length;
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${maxImages} photos allowed`);
      return;
    }

    // Client-side validation — matches backend ALLOWED_EXTENSIONS
    const ALLOWED = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const invalid = files.find((f) => {
      const ext = (f.name || '').toLowerCase().slice(((f.name || '').lastIndexOf('.') || -1) >>> 0);
      return !ALLOWED.includes(ext);
    });
    if (invalid) {
      const name = (invalid.name || '').toLowerCase();
      if (name.endsWith('.heic') || name.endsWith('.heif')) {
        toast.error('HEIC photos aren\u2019t supported. On iPhone: Settings \u2192 Camera \u2192 Formats \u2192 Most Compatible. Or convert to JPG first.');
      } else {
        toast.error(`Only JPG, PNG, GIF, or WEBP photos are supported (got ${invalid.name || 'unknown'}).`);
      }
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    setUploading(true);

    try {
      const uploadedImages = [];

      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post('/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        // Store the full image object {ref, url, expires_at}
        // This gets saved to the database for cached URL lookups
        if (response.data.image) {
          uploadedImages.push(response.data.image);
        } else {
          // Fallback for old format
          const ref = response.data.url;
          const displayUrl = response.data.signed_url || 
            (ref.startsWith('http') ? ref : `${API_URL}${ref}`);
          uploadedImages.push({
            ref: ref,
            url: displayUrl,
            expires_at: null
          });
        }
      }

      const newImages = [...images, ...uploadedImages];
      onImagesChange(newImages);
      
      // If this is the first image, set it as primary
      if (images.length === 0 && uploadedImages.length > 0) {
        onPrimaryChange(0);
      }
      
      toast.success(`${uploadedImages.length} photo${uploadedImages.length > 1 ? 's' : ''} uploaded`);
    } catch (error) {
      console.error('Upload failed:', error);
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
    
    // Adjust primary index if needed
    if (primaryIndex === index) {
      onPrimaryChange(0);
    } else if (primaryIndex > index) {
      onPrimaryChange(primaryIndex - 1);
    }
  };

  const setPrimary = (index) => {
    onPrimaryChange(index);
    toast.success('Primary photo updated');
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newImages = [...images];
    const [draggedImage] = newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);
    onImagesChange(newImages);

    // Update primary index if it was affected by the reorder
    let newPrimaryIndex = primaryIndex;
    if (draggedIndex === primaryIndex) {
      newPrimaryIndex = dropIndex;
    } else if (draggedIndex < primaryIndex && dropIndex >= primaryIndex) {
      newPrimaryIndex = primaryIndex - 1;
    } else if (draggedIndex > primaryIndex && dropIndex <= primaryIndex) {
      newPrimaryIndex = primaryIndex + 1;
    }
    onPrimaryChange(newPrimaryIndex);
    
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-4">
      {/* Photo Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
        {images.map((image, index) => (
          <div
            key={index}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            className={`relative aspect-square rounded-xl overflow-hidden group cursor-move border-2 transition-all ${
              primaryIndex === index 
                ? 'border-[#C9956C] ring-2 ring-[#C9956C]/30' 
                : 'border-[var(--theme-border)] hover:border-[var(--theme-text-muted)]'
            } ${draggedIndex === index ? 'opacity-50' : ''}`}
          >
            <img 
              src={getDisplayUrl(image)} 
              alt={`Photo ${index + 1}`}
              className="w-full h-full object-cover"
            />
            
            {/* Primary Badge */}
            {primaryIndex === index && (
              <div className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full bg-[#C9956C] text-white text-[10px] font-bold flex items-center gap-1">
                <Star className="w-3 h-3 fill-current" />
                PRIMARY
              </div>
            )}

            {/* Drag Handle */}
            <div className="absolute top-1.5 right-1.5 p-1 rounded bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
              <GripVertical className="w-4 h-4 text-white" />
            </div>

            {/* Hover Actions */}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              {primaryIndex !== index && (
                <button
                  type="button"
                  onClick={() => setPrimary(index)}
                  className="p-2 rounded-full bg-[#C9956C] text-white hover:bg-[#B8845B] transition-colors"
                  title="Set as primary"
                >
                  <Star className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => removeImage(index)}
                className="p-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                title="Remove"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}

        {/* Add Photo Button - only in non-compact mode */}
        {!compact && images.length < maxImages && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="aspect-square rounded-xl border-2 border-dashed border-[var(--theme-border)] hover:border-[#C9956C] bg-[var(--theme-hover)] flex flex-col items-center justify-center gap-2 transition-colors cursor-pointer"
          >
            {uploading ? (
              <Loader2 className="w-6 h-6 animate-spin text-[#C9956C]" />
            ) : (
              <>
                <Plus className="w-6 h-6 text-[var(--theme-text-muted)]" />
                <span className="text-xs text-[var(--theme-text-muted)]">Add Photo</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Upload Area (when no photos OR in compact mode with available slots) */}
      {(images.length === 0 || (compact && images.length < maxImages)) && (
        <div 
          onClick={() => fileInputRef.current?.click()}
          className={`rounded-xl border-2 border-dashed border-[var(--theme-border)] hover:border-[#C9956C] bg-[var(--theme-hover)] cursor-pointer transition-colors text-center ${
            images.length > 0 ? 'p-4' : 'p-8'
          }`}
        >
          {images.length === 0 ? (
            <>
              <div className="w-16 h-16 rounded-full bg-[#C9956C]/10 flex items-center justify-center mx-auto mb-3">
                <Camera className="w-8 h-8 text-[#C9956C]" />
              </div>
              <h4 className="font-semibold text-[var(--theme-text-primary)] mb-1">Add Photos</h4>
              <p className="text-sm text-[var(--theme-text-muted)] mb-3">
                Click to upload • Max {maxImages} photos
              </p>
              <Button 
                type="button"
                disabled={uploading}
                className="bg-[#C9956C] hover:bg-[#B8845B] rounded-full"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Camera className="w-4 h-4 mr-2" />
                    Select Photos
                  </>
                )}
              </Button>
            </>
          ) : (
            <div className="flex items-center justify-center gap-2 py-2">
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin text-[#C9956C]" />
              ) : (
                <Plus className="w-4 h-4 text-[#C9956C]" />
              )}
              <span className="text-sm text-[var(--theme-text-secondary)]">
                {uploading ? 'Uploading...' : `Add more photos (${images.length}/${maxImages})`}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Help Text */}
      {images.length > 0 && (
        <p className="text-xs text-[var(--theme-text-muted)]">
          <Star className="w-3 h-3 inline text-[#C9956C] mr-1" />
          The primary photo will be shown as the main display image. Drag to reorder.
        </p>
      )}
    </div>
  );
}
